import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import {
  RefreshCw, Loader2, AlertTriangle, Phone, MessageSquare, Mail,
  IndianRupee, Clock, UserX, CheckCircle2, X, CalendarClock, PencilLine,
} from 'lucide-react';

// The closing desk — what is nearly money, and what has gone quiet.
//
// Built 29 Aug 2026 after pricing every open mandate: DCS holds 33 open
// mandates worth ₹4.73 Cr of annual CTC, ten of them with no CV attached at
// all, and ten candidates sitting on live offers. None of that was visible on
// a screen. This page is that screen. It sends nothing; it records what was
// said and shows what has gone quiet.

const inr = (v) => {
  if (v === null || v === undefined || v === 0) return '—';
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(2)} Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)} L`;
  return `₹${Number(v).toLocaleString('en-IN')}`;
};

const STAGE = {
  offered:     { label: 'Offer out',  cls: 'bg-emerald-100 text-emerald-800' },
  lined_up:    { label: 'Lined up',   cls: 'bg-amber-100 text-amber-800' },
  interviewed: { label: 'Interviewed', cls: 'bg-sky-100 text-sky-700' },
};

const CHANNELS = [
  { id: 'call', label: 'Called', Icon: Phone },
  { id: 'whatsapp', label: 'WhatsApp', Icon: MessageSquare },
  { id: 'email', label: 'Emailed', Icon: Mail },
  { id: 'meeting', label: 'Met', Icon: CalendarClock },
  { id: 'note', label: 'Note', Icon: PencilLine },
];

const OUTCOMES = [
  { id: 'reached', label: 'Spoke to them' },
  { id: 'no_answer', label: 'No answer' },
  { id: 'joining', label: 'Joining — date agreed' },
  { id: 'declined', label: 'Declined the offer' },
  { id: 'dropped', label: 'Dropped out' },
  { id: 'note', label: 'Just a note' },
];

const Stat = ({ label, value, sub, accent }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
    <p className="text-[11px] sm:text-xs text-slate-500 uppercase tracking-wide leading-tight">{label}</p>
    <p className={`text-xl sm:text-2xl font-bold mt-1 ${accent || 'text-slate-900'}`}>{value}</p>
    {sub ? <p className="text-[11px] text-slate-400 mt-0.5 leading-tight">{sub}</p> : null}
  </div>
);

// How long a row has been where it is — and, honestly, which clock that is.
// A record's age and a stage's age are different claims; the ATS only started
// keeping the second one on 29 Aug, so until it has history the badge says so.
const Age = ({ days, basis }) => {
  if (days === null || days === undefined) return <span className="text-slate-400">—</span>;
  const hot = days >= 30;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <Clock className={`w-3 h-3 ${hot ? 'text-red-500' : 'text-slate-400'}`} />
      <span className={hot ? 'text-red-600 font-semibold' : 'text-slate-600'}>{days}d</span>
      {basis === 'record' && (
        <span className="text-[10px] text-slate-400" title="Age of the record. The stage clock starts once a stage change is seen.">
          on file
        </span>
      )}
    </span>
  );
};

export default function ClosingPage() {
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('people');
  const [touch, setTouch] = useState(null);      // the person being logged
  const [form, setForm] = useState({ channel: 'call', outcome: 'reached', note: '' });
  const [saving, setSaving] = useState(false);
  const [feeDraft, setFeeDraft] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/closing/board');
      setBoard(r.data);
      // Blank means no override: every mandate is priced from the rate card by
      // its own CTC.  The board reports the override as `fee_percent_override`,
      // and it is null far more often than not.
      const ov = r.data.fee_percent_override;
      setFeeDraft(ov === null || ov === undefined ? '' : String(ov));
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not load the closing desk.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveFee = async () => {
    // An empty box hands pricing back to the rate card - a real choice, not an
    // error, so it is allowed through rather than rejected.
    const blank = feeDraft.trim() === '';
    const pct = parseFloat(feeDraft);
    if (!blank && !(pct > 0 && pct <= 100)) {
      setError('Fee must be between 0 and 100, or blank to use the rate card.');
      return;
    }
    try {
      await api.put('/closing/settings', { fee_percent: blank ? null : pct });
      setLoading(true); await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save the fee.');
    }
  };

  const saveTouch = async () => {
    if (!touch) return;
    setSaving(true);
    try {
      await api.post('/closing/touch', {
        candidate_id: touch.candidate_id,
        requirement_id: touch.requirement_id,
        channel: form.channel, outcome: form.outcome, note: form.note,
      });
      setTouch(null);
      setForm({ channel: 'call', outcome: 'reached', note: '' });
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not record that.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Pricing every open mandate…
      </div>
    );
  }

  const t = board?.totals || {};
  const people = board?.people || [];
  const mandates = board?.mandates || [];
  const unstaffed = mandates.filter((m) => m.unstaffed);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-5" data-testid="closing-page">

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Closing desk</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            What is nearly money, and what has gone quiet.
          </p>
        </div>
        <button onClick={() => { setLoading(true); load(); }}
          className="inline-flex items-center gap-2 px-3 min-h-[44px] text-sm font-medium rounded-lg
                     border border-slate-200 bg-white text-slate-700 hover:bg-slate-50">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error ? (
        <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700
                        rounded-lg px-3 py-2 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      ) : null}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Fee if every seat fills" value={inr(t.fee_if_all_filled)}
              sub={`${t.open_mandates} mandates · ${t.seats} seats`} accent="text-emerald-700" />
        <Stat label="Offers out" value={inr(t.offered_fee)}
              sub={`${t.offered_people} people`} accent="text-emerald-700" />
        <Stat label="Lined up" value={inr(t.lined_up_fee)}
              sub={`${t.lined_up_people} people`} accent="text-amber-700" />
        <Stat label="No CV attached"
              value={t.unstaffed_mandates === null ? '—' : t.unstaffed_mandates}
              sub="mandates nobody has started"
              accent={t.unstaffed_mandates ? 'text-red-600' : 'text-slate-900'} />
      </div>

      {board?.pipeline_collection === null ? (
        <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800
                        rounded-lg px-3 py-2 text-sm">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Candidate pipeline records could not be found, so candidate counts are blank
            rather than zero. <code>/api/closing/diagnose</code> lists what was checked.</span>
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-slate-500">Fee</span>
        <div className="inline-flex items-center gap-1 border border-slate-200 rounded-lg
                        bg-white px-3 min-h-[44px]">
          <input value={feeDraft} onChange={(e) => setFeeDraft(e.target.value)}
                 inputMode="decimal" aria-label="Fee percentage" placeholder="rate card"
                 className="w-20 text-right outline-none text-slate-900 font-semibold
                            placeholder:font-normal placeholder:text-slate-400" />
          <span className="text-slate-400">% of CTC</span>
        </div>
        <button onClick={saveFee}
          className="px-4 min-h-[44px] rounded-lg bg-slate-900 text-white text-sm font-medium">
          Apply
        </button>
        {feeDraft.trim() ? (
          <button onClick={() => { setFeeDraft(''); }}
            className="px-3 min-h-[44px] rounded-lg border border-slate-200 bg-white
                       text-sm text-slate-600">
            Clear
          </button>
        ) : null}
        <span className="text-xs text-slate-400">
          {feeDraft.trim()
            ? 'Every figure on this page follows it.'
            : 'Priced from the rate card - each mandate by its own CTC.'}
        </span>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {[['people', `Needs a call (${people.length})`],
          ['mandates', `Mandates (${mandates.length})`]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3 min-h-[44px] text-sm font-medium border-b-2 -mb-px ${
              tab === id ? 'border-slate-900 text-slate-900'
                         : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'people' ? (
        <div className="space-y-2">
          {people.length === 0 ? (
            <p className="text-sm text-slate-500 py-6">
              Nobody is at offer, lined up or interviewed on an open mandate.
            </p>
          ) : people.map((p) => {
            const s = STAGE[p.stage] || { label: p.stage, cls: 'bg-slate-100 text-slate-600' };
            return (
              <div key={`${p.requirement_id}:${p.candidate_id}`}
                   className="bg-white border border-slate-200 rounded-xl p-3 sm:p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${s.cls}`}>
                        {s.label}
                      </span>
                      <span className="font-semibold text-slate-900">{p.name || 'Unnamed'}</span>
                      <Age days={p.age_days} basis={p.age_basis} />
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {p.role}{p.client ? <span className="text-slate-400"> · {p.client}</span> : null}
                    </p>
                    {p.last_touch ? (
                      <p className="text-xs text-slate-500 mt-1">
                        Last chased {p.last_touch_days}d ago — {p.last_touch_outcome}
                      </p>
                    ) : (
                      <p className="text-xs text-red-600 mt-1 font-medium">Never chased</p>
                    )}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-semibold text-emerald-700 tabular-nums">{inr(p.fee)}</p>
                    <p className="text-[11px] text-slate-400">fee if they join</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {p.phone ? (
                    <a href={`tel:${p.phone}`}
                       className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg text-sm
                                  font-medium bg-slate-900 text-white">
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                  ) : null}
                  {p.phone ? (
                    <a href={`https://wa.me/91${String(p.phone).replace(/\D/g, '').slice(-10)}`}
                       target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg text-sm
                                  font-medium border border-slate-200 bg-white text-slate-700">
                      <MessageSquare className="w-3.5 h-3.5" /> WhatsApp
                    </a>
                  ) : null}
                  {p.client_email ? (
                    <a href={`mailto:${p.client_email}?subject=${encodeURIComponent(p.role || '')}`}
                       className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg text-sm
                                  font-medium border border-slate-200 bg-white text-slate-700">
                      <Mail className="w-3.5 h-3.5" /> Ask the client
                    </a>
                  ) : null}
                  <button onClick={() => setTouch(p)}
                    className="inline-flex items-center gap-1.5 px-4 min-h-[44px] rounded-lg text-sm
                               font-medium border border-slate-200 bg-white text-slate-700">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Log what happened
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {unstaffed.length ? (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-800
                            rounded-lg px-3 py-2 text-sm">
              <UserX className="w-4 h-4 mt-0.5 shrink-0" />
              <span><b>{unstaffed.length} open mandates have no candidate attached at all.</b>{' '}
                Close them or staff them — they are at the top of this list.</span>
            </div>
          ) : null}
          {mandates.map((m) => (
            <div key={m.requirement_id}
                 className={`bg-white border rounded-xl p-3 sm:p-4 ${
                   m.unstaffed ? 'border-red-200' : 'border-slate-200'}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900">{m.role}</p>
                  <p className="text-sm text-slate-500">
                    {m.client}{m.owner && m.owner !== 'Unknown'
                      ? <span className="text-slate-400"> · {m.owner}</span> : null}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-semibold text-slate-900 tabular-nums">{inr(m.fee)}</p>
                  <p className="text-[11px] text-slate-400">
                    {m.seats} seat{m.seats === 1 ? '' : 's'} · {inr(m.ctc_per_seat)} each
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                <span>{m.candidates === null ? '—' : m.candidates} CV{m.candidates === 1 ? '' : 's'}</span>
                <span>opened {m.opened_days}d ago</span>
                {m.idle_days !== null && m.idle_days !== undefined
                  ? <span className={m.stale ? 'text-red-600 font-semibold' : ''}>
                      no new CV for {m.idle_days}d</span>
                  : null}
                {m.unstaffed
                  ? <span className="text-red-600 font-semibold">not one CV attached</span>
                  : null}
              </div>
            </div>
          ))}
        </div>
      )}

      {touch ? (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-start
                        justify-center p-0 sm:p-4 overflow-y-auto"
             onClick={() => setTouch(null)}>
          <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-lg w-full
                          sm:mt-16 p-4 sm:p-6 space-y-4"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h2 className="font-bold text-slate-900">{touch.name}</h2>
                <p className="text-sm text-slate-500">{touch.role} · {touch.client}</p>
              </div>
              <button onClick={() => setTouch(null)} aria-label="Close"
                className="p-1 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">How</p>
              <div className="flex flex-wrap gap-1.5">
                {CHANNELS.map(({ id, label, Icon }) => (
                  <button key={id} onClick={() => setForm({ ...form, channel: id })}
                    className={`inline-flex items-center gap-1.5 px-3 min-h-[44px] rounded-lg text-sm ${
                      form.channel === id ? 'bg-slate-900 text-white'
                                          : 'border border-slate-200 text-slate-700'}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                What happened
              </p>
              <div className="flex flex-wrap gap-1.5">
                {OUTCOMES.map(({ id, label }) => (
                  <button key={id} onClick={() => setForm({ ...form, outcome: id })}
                    className={`px-3 min-h-[44px] rounded-lg text-sm ${
                      form.outcome === id ? 'bg-slate-900 text-white'
                                          : 'border border-slate-200 text-slate-700'}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <textarea value={form.note} rows={3} maxLength={2000}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder="What did they say?"
              className="w-full border border-slate-200 rounded-lg p-3 text-sm outline-none
                         focus:border-slate-400" />

            <p className="text-xs text-slate-400">
              This records the conversation. It does not send anything.
            </p>

            <button onClick={saveTouch} disabled={saving}
              className="w-full min-h-[48px] rounded-lg bg-slate-900 text-white font-semibold
                         disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Save'}
            </button>
          </div>
        </div>
      ) : null}

      <p className="text-xs text-slate-400 pt-2">
        <IndianRupee className="w-3 h-3 inline -mt-0.5" /> Fees are {board?.fee_percent}% of
        annual CTC. {board?.excluded_test_mandates
          ? `${board.excluded_test_mandates} internal test mandate(s) excluded.` : ''}
      </p>
    </div>
  );
}
